/**
 * SPEC UNIQUE de mise en forme rédaction — source de vérité lue par les
 * producteurs de contenu du canvas (`editor/blocksToHtml.ts`,
 * `editor/canvasDocumentWrite.ts`, `editor/canvasLetterhead.ts`, `RedactionCanvas`).
 *
 * **Invariant produit (validé @user 2026-06-08)** : ce que l'utilisateur voit à
 * l'écran DOIT être identique à 100% à ce qu'il télécharge en `.docx`. Toute
 * divergence est un bug. Donc UNE seule table de valeurs, exprimée en unités
 * physiques (pt / mm), convertie par chaque consommateur.
 *
 * **Discipline** :
 *  - Tout changement de mise en forme passe par CE fichier — JAMAIS directement
 *    dans les producteurs (`blocksToHtml.ts`, `canvasDocumentWrite.ts`), qui ne
 *    font que consommer la spec.
 *  - Les overrides agent (`element.align`, `element.fontFamily`, `element.fontSize`)
 *    s'appliquent PAR-DESSUS la spec dans les 2 sorties → ils restent fidèles.
 */

import type { HectorStyleKey } from '@hector/shared-types';

/** Police d'écriture d'Hector quand le cabinet n'en choisit pas : la tête de {@link FONT_FAMILY_CSS}. */
export const POLICE_D_ECRITURE = 'EB Garamond';
export const FONT_FAMILY_CSS = `'${POLICE_D_ECRITURE}', Garamond, 'Times New Roman', serif`;
/**
 * Interligne des styles de CORPS (Normal, Compact, Quote, Liste, Pièce citée, Ligne de marge,
 * Custom), posé par style dans le gabarit (`w:line` = 240 × cette valeur, `lineRule="auto"`).
 * Même périmètre et même valeur que l'add-in Word (`setupHectorStyles.ts`, `applyFormatting`,
 * `DEFAULT_PARAGRAPH_SETTINGS.lineSpacing`) : les lignes rituelles courtes restent serrées.
 *
 * Le gabarit est la seule source : `blocksToHtml` n'émet aucun `line-height`, que SuperDoc
 * poserait à la main par-dessus le style. Titres et marqueurs gardent l'interligne simple du
 * `pPrDefault`. L'interligne simple du corps (jusqu'au 2026-10-23) rendait un courrier en bloc
 * dense, signalé par une avocate.
 */
export const INTERLIGNE_DU_CORPS = 1.15;
/** Marge page A4 standard = 2cm = 1134 twips = 20mm. */
export const PAGE_MARGIN_MM = 20;
/**
 * Padding interne H1 encadré — aligné `DEFAULT_TITLE_BOX_SETTINGS.cellPadding=5`
 * add-in (`cabinet.ts:335`). 5pt ≈ 1.76mm de marge intérieure dans la table 1×1.
 */
export const H1_BOX_PADDING_PT = 5;

export interface RedactionStyleSpec {
  /** Taille en POINTS (12, 13, 15…). Convertie en demi-pt pour DOCX, en `Npt` pour CSS. */
  size_pt: number;
  bold?: boolean;
  underline?: boolean;
  italic?: boolean;
  alignment?: 'left' | 'center' | 'right' | 'justify';
  /** Rendu encadré (border 1px noir 4 côtés + padding interne H1_BOX_PADDING_PT). */
  boxed?: boolean;
  /** Force un saut de page avant ce bloc (DOCX) et un séparateur visuel (canvas). */
  pageBreakBefore?: boolean;
  /** Espace AVANT le bloc, en POINTS. Converti en twips (×20) ou px (×96/72). */
  spacingBefore_pt?: number;
  /** Espace APRÈS le bloc, en POINTS. */
  spacingAfter_pt?: number;
  /**
   * Barre verticale en marge gauche (« ligne de marge »), OOXML `w:pBdr/w:left`.
   *
   * ⚠️ **Ces valeurs ne sont pas choisies ici, elles sont RECOPIÉES.** Source :
   * `injectLeftBorderIntoOoxml` (`apps/addin-word/src/services/insertion/wordInserter.ts:113`),
   * la barre de révision de réplique déjà en production dans l'add-in Word — relevée le
   * 2026-08-16, `w:sz="18"` (huitièmes de point, soit 2,25 pt) `w:space="14"` (points)
   * `w:color="000000"`. C'est ce qui rend la parité add-in ↔ canvas garantie par
   * construction au lieu d'être promise, et c'est là qu'il faut aller la re-vérifier si
   * l'add-in change sa barre.
   *
   * Contrainte qui interdit de faire diverger les deux : Word ne fusionne deux barres de
   * paragraphes adjacents en une seule ligne continue que si les `w:pBdr` sont
   * **strictement** identiques (design du 2026-07-04, `docs/features/act-generation/specs/
   * 2026-07-04-conclusions-replique-changebar-design.html`).
   */
  leftBorder?: {
    /** Épaisseur du trait en POINTS. OOXML `w:sz` est en huitièmes de point (×8). */
    width_pt: number;
    /** Écart trait ↔ texte en POINTS. OOXML `w:space` est en points (entier). */
    space_pt: number;
    /** Couleur hexadécimale sans `#`, convention OOXML. */
    color: string;
  };
}

/**
 * Spec unique des styleKey Hector — **strict mirror des valeurs
 * `DEFAULT_*` du cabinet add-in** (`apps/addin-word/src/types/cabinet.ts:147-322`).
 *
 * Choix produit user 2026-06-09 (option A) : parité visuelle stricte entre
 * canvas web et add-in Word. Le canvas ressemble pixel-perfect à ce que
 * l'utilisateur recevra côté add-in / acte exporté. Toute clé absente
 * retombe sur `Hector-Normal` (11pt justify).
 *
 * Mapping cabinet.ts → spec.ts :
 *  - `headings.h1..h6` → `Hector-H1..H6` (size_pt = fontSize)
 *  - `paragraph` → `Hector-Normal` + `Hector-Normal-Compact`
 *  - `markers.plaise/pour/contre/devant/objet` → `Hector-PLAISE/Marqueur-*`
 *  - `markers.bordereau` → `Hector-BORDEREAU`
 *  - `markers.pieceBeforeBordereau/pieceAfterBordereau` → `Hector-Piece-*`
 *  - `markers.demanderesse` → `Hector-Party-Label`
 *  - `markers.defenderesse` → `Hector-Defenderesse` (style propre depuis 2026-07-29)
 *  - `markers.delimiterUnderline/delimiterBold` → `Hector-Delimiter-Underline/-Bold`
 *  - `markers.parCesMotifs` → `Hector-PAR-CES-MOTIFS`
 *  - `markers.tresImportant` → `Hector-TRES-IMPORTANT`
 *  - `titleBox.enabled` → flag `boxed` sur Hector-H1
 */
export const REDACTION_STYLE_SPEC: Partial<Record<HectorStyleKey | 'p', RedactionStyleSpec>> = {
  // ─── Headings (cabinet.ts:150-205) ────────────────────────────────────
  // H1 : 14pt bold center (boxed via titleBox.enabled=true par défaut)
  'Hector-H1': { size_pt: 14, bold: true, alignment: 'center', boxed: true, spacingBefore_pt: 10, spacingAfter_pt: 8 },
  // H2 : 14pt bold single-underline justified
  'Hector-H2': { size_pt: 14, bold: true, underline: true, alignment: 'justify', spacingBefore_pt: 8, spacingAfter_pt: 8 },
  // H3 : 12pt bold justified
  'Hector-H3': { size_pt: 12, bold: true, alignment: 'justify', spacingBefore_pt: 8, spacingAfter_pt: 6 },
  // H4-6 : 11pt bold justified (cabinet.ts:178-204)
  'Hector-H4': { size_pt: 11, bold: true, alignment: 'justify', spacingBefore_pt: 8, spacingAfter_pt: 6 },
  'Hector-H5': { size_pt: 11, bold: true, alignment: 'justify', spacingBefore_pt: 6, spacingAfter_pt: 6 },
  'Hector-H6': { size_pt: 11, bold: true, alignment: 'justify', spacingBefore_pt: 6, spacingAfter_pt: 6 },

  // ─── Body (cabinet.ts:208-215) ────────────────────────────────────────
  // Normal : 11pt justified, spaceAfter=6
  'Hector-Normal': { size_pt: 11, alignment: 'justify', spacingAfter_pt: 6 },
  // Normal-Compact : 11pt justified, spaceAfter=0 (cabinet.ts:212 spaceAfterReduced=0)
  'Hector-Normal-Compact': { size_pt: 11, alignment: 'justify', spacingAfter_pt: 0 },
  'Hector-Quote': { size_pt: 11, italic: true, alignment: 'justify', spacingBefore_pt: 6, spacingAfter_pt: 6 },
  'Hector-Liste': { size_pt: 11, alignment: 'justify', spacingAfter_pt: 6 },

  // ─── PLAISE / PLAISE-Body (cabinet.ts:219-226) ────────────────────────
  // PLAISE 1er : bold center + page break + lineBreakAfter true → trailingBreak.
  // fontSize hérite paragraph default 11pt (markers.plaise n'override pas).
  'Hector-PLAISE': {
    size_pt: 11,
    bold: true,
    alignment: 'center',
    pageBreakBefore: true,
    spacingBefore_pt: 0,
    spacingAfter_pt: 6,
  },
  // PLAISE-Body : justified compact (paragraphFormatter.ts:221 add-in)
  'Hector-PLAISE-Body': { size_pt: 11, alignment: 'justify', spacingBefore_pt: 3, spacingAfter_pt: 3 },

  // ─── BORDEREAU + PIÈCES JOINTES (cabinet.ts:259-269) ──────────────────
  // BORDEREAU : 11pt bold left, spacing 0/6, page break before
  'Hector-BORDEREAU': {
    size_pt: 11,
    bold: true,
    pageBreakBefore: true,
    spacingBefore_pt: 0,
    spacingAfter_pt: 6,
  },
  // Pièces-Jointes : 11pt bold underline, page break before
  'Hector-Pieces-Jointes': {
    size_pt: 11,
    bold: true,
    underline: true,
    pageBreakBefore: true,
    spacingBefore_pt: 0,
    spacingAfter_pt: 6,
  },

  // ─── Marqueurs juridiques (cabinet.ts:227-321) ────────────────────────
  // PAR CES MOTIFS : 12pt bold center, spaceBefore/After 12 (cabinet.ts:303-312)
  'Hector-PAR-CES-MOTIFS': {
    size_pt: 12,
    bold: true,
    alignment: 'center',
    spacingBefore_pt: 12,
    spacingAfter_pt: 12,
  },
  // TRÈS IMPORTANT : 11pt bold + single underline left (cabinet.ts:314-321)
  'Hector-TRES-IMPORTANT': {
    size_pt: 11,
    bold: true,
    underline: true,
    spacingBefore_pt: 0,
    spacingAfter_pt: 6,
  },
  // Marqueur-BU (POUR/CONTRE/DEVANT) : 11pt bold + single underline left
  // (cabinet.ts:227-249 — pour/contre/devant)
  'Hector-Marqueur-BU': {
    size_pt: 11,
    bold: true,
    underline: true,
    spacingBefore_pt: 0,
    spacingAfter_pt: 6,
  },
  // Marqueur-Bold (OBJET, EXPÉDITEUR, J'AI DONNÉ ASSIGNATION, etc.) :
  // 11pt bold left, no underline (cabinet.ts:251-258 — objet)
  'Hector-Marqueur-Bold': { size_pt: 11, bold: true, spacingBefore_pt: 0, spacingAfter_pt: 6 },
  // Party-Label (DEMANDERESSE) : 11pt bold italic right (cabinet.ts:348-355)
  'Hector-Party-Label': {
    size_pt: 11,
    bold: true,
    italic: true,
    alignment: 'right',
    spacingBefore_pt: 0,
    spacingAfter_pt: 6,
  },
  // Défenderesse : style PROPRE depuis 2026-07-29 — ce n'est plus un alias de Party-Label.
  // Valeurs recopiées de `DEFAULT_MARKER_STYLES.defenderesse` (cabinet.ts:356-363) : elles
  // sont aujourd'hui identiques à celles de `demanderesse`, et c'est exactement l'intérêt
  // d'un style distinct — le cabinet qui édite sa `MarkerStyle` défenderesse voit enfin sa
  // ligne bouger sans emporter la demanderesse. Tant que les deux partageaient
  // `Hector-Party-Label`, cette édition était un no-op silencieux (corrigé côté add-in par
  // `styleResolver.ts:54`, `marker-defenderesse` → `Hector-Defenderesse`).
  'Hector-Defenderesse': {
    size_pt: 11,
    bold: true,
    italic: true,
    alignment: 'right',
    spacingBefore_pt: 0,
    spacingAfter_pt: 6,
  },

  // ─── Délimiteurs d'acte (cabinet.ts:384-397) ──────────────────────────
  // Les deux classes éditables où retombent les formules de préambule que l'add-in codait
  // en dur (2026-07-29) : `delimiterUnderline` = gras + souligné + gauche (À LA REQUÊTE DE,
  // ASSISTÉ(E) DE, DÉCISION ATTAQUÉE, Objet de la demande…) ; `delimiterBold` = gras seul +
  // gauche (DEMEURANT, J'AI, TOTAL DÛ, AYANT POUR AVOCAT…). Mêmes valeurs que les defaults
  // web (`src/types/formatting.ts:292-293`), dont cabinet.ts se déclare le miroir 1:1.
  //
  // Mise en forme identique à `Hector-Marqueur-BU` / `Hector-Marqueur-Bold`, mais un style à
  // part : c'est ce qui permet au cabinet de restyler ses délimiteurs sans toucher à
  // POUR / CONTRE / DEVANT ni à OBJET. L'alignement est écrit explicitement (là où les deux
  // marqueurs jumeaux, plus anciens, le laissent implicite) parce que l'add-in l'applique
  // explicitement au runtime depuis `MarkerStyle.alignment = 'left'`.
  'Hector-Delimiter-Underline': {
    size_pt: 11,
    bold: true,
    underline: true,
    alignment: 'left',
    spacingBefore_pt: 0,
    spacingAfter_pt: 6,
  },
  'Hector-Delimiter-Bold': {
    size_pt: 11,
    bold: true,
    alignment: 'left',
    spacingBefore_pt: 0,
    spacingAfter_pt: 6,
  },

  // ─── Pièces (cabinet.ts:270-286) ──────────────────────────────────────
  // Pieces-Before-Bordereau : bold right (cabinet.ts:270-277)
  'Hector-Piece-Before': { size_pt: 11, bold: true, alignment: 'right', spacingBefore_pt: 0, spacingAfter_pt: 6 },
  // Pieces-After-Bordereau : 11pt normal left, no breaks (cabinet.ts:278-286)
  'Hector-Piece-After': { size_pt: 11, alignment: 'left', spacingBefore_pt: 0, spacingAfter_pt: 6 },
  // ─── Pièce citée (#989) ───────────────────────────────────────────────
  // ⚠️ ARBITRAGE PRODUIT, pas une valeur sourcée : ni #989 ni la §4 « Les styles » de la
  // spec #985 ne donnent la mise en forme visée — elles nomment le style et se taisent sur
  // son rendu. Tranché par le porteur le 2026-08-16 : italique, aligné à gauche, sans
  // retrait.
  //
  // Ce que le style désigne, et qui explique l'italique : un paragraphe qui **reproduit un
  // extrait d'une pièce** du dossier (un contrat, un courrier, un PV cité dans le corps de
  // l'acte) — l'italique est la convention française de la citation détachée. Il ne fait
  // donc pas doublon avec les deux styles pièce existants, qui traitent la *mention* d'une
  // pièce et non son *contenu* : `Hector-Piece-Before` (gras, aligné à droite) est la
  // convention « pièce » du corps d'acte, `Hector-Piece-After` la ligne de bordereau.
  //
  // Adjacence connue et assumée : `Hector-Quote` est lui aussi italique. Il porte la
  // citation générique (justifiée) ; celui-ci porte la citation ATTRIBUÉE à une pièce du
  // dossier, alignée à gauche.
  //
  // Le RETRAIT GAUCHE est volontairement hors périmètre : il exigerait un champ
  // d'indentation nouveau dans `RedactionStyleSpec`, alors que #989 dit « il s'agit de
  // l'étendre, pas de construire un mécanisme ». Un champ d'indentation partagé est un
  // sujet en soi, qui touchera aussi le pinceau élargi (#991, `setIndentation`).
  'Hector-Piece-Citee': {
    size_pt: 11,
    italic: true,
    alignment: 'left',
    spacingBefore_pt: 0,
    spacingAfter_pt: 6,
  },

  // ─── Separator ────────────────────────────────────────────────────────
  'Hector-Separator': { size_pt: 11, alignment: 'center', spacingBefore_pt: 6, spacingAfter_pt: 6 },

  // ─── Ligne de marge (#989) ────────────────────────────────────────────
  // Barre verticale noire en marge gauche, le long du paragraphe : la barre de révision
  // d'une réplique (« ce passage est nouveau »). Corps identique à `Hector-Normal` — seule
  // la barre distingue. Valeurs reprises de l'add-in (cf. `leftBorder` ci-dessus) : des
  // paragraphes adjacents portant un `w:pBdr` IDENTIQUE voient Word fusionner le trait en
  // une barre continue, ce qui n'arrive que si les deux hôtes écrivent les mêmes valeurs.
  'Hector-Ligne-Marge': {
    size_pt: 11,
    alignment: 'justify',
    spacingBefore_pt: 0,
    spacingAfter_pt: 6,
    leftBorder: { width_pt: 2.25, space_pt: 14, color: '000000' },
  },

  // ─── Custom1..10 — slots customisables par template_pack cabinet ──────
  // Fallback Normal (11pt justify) en l'absence d'override
  // `FormattingSettings.styleOverrides` runtime.
  'Hector-Custom1': { size_pt: 11, alignment: 'justify', spacingAfter_pt: 6 },
  'Hector-Custom2': { size_pt: 11, alignment: 'justify', spacingAfter_pt: 6 },
  'Hector-Custom3': { size_pt: 11, alignment: 'justify', spacingAfter_pt: 6 },
  'Hector-Custom4': { size_pt: 11, alignment: 'justify', spacingAfter_pt: 6 },
  'Hector-Custom5': { size_pt: 11, alignment: 'justify', spacingAfter_pt: 6 },
  'Hector-Custom6': { size_pt: 11, alignment: 'justify', spacingAfter_pt: 6 },
  'Hector-Custom7': { size_pt: 11, alignment: 'justify', spacingAfter_pt: 6 },
  'Hector-Custom8': { size_pt: 11, alignment: 'justify', spacingAfter_pt: 6 },
  'Hector-Custom9': { size_pt: 11, alignment: 'justify', spacingAfter_pt: 6 },
  'Hector-Custom10': { size_pt: 11, alignment: 'justify', spacingAfter_pt: 6 },

  p: { size_pt: 11, alignment: 'justify', spacingAfter_pt: 6 },
};

export function getSpec(styleKey: string): RedactionStyleSpec {
  return REDACTION_STYLE_SPEC[styleKey as HectorStyleKey | 'p'] ?? REDACTION_STYLE_SPEC['Hector-Normal']!;
}
